import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { PlantusLogo } from "@/components/plantus/Logo";
import { SignIn } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Willkommen zurück");
      navigate(location.state?.from || "/app", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest-900">
        Willkommen zurück
      </h1>
      <p className="mt-2 font-body text-[15px] text-[#4A524C]">
        Melden Sie sich mit den Daten Ihrer Plantus-App an.
      </p>

      <form onSubmit={onSubmit} data-testid="login-form" className="mt-8 space-y-5">
        <TextField
          label="E-Mail"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          testId="login-email-input"
        />
        <TextField
          label="Passwort"
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          testId="login-password-input"
        />
        {error && (
          <div
            data-testid="login-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-heading font-medium px-6 py-3 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5"
        >
          <SignIn size={18} weight="bold" />
          {loading ? "Anmelden …" : "Anmelden"}
        </button>
        <div className="text-right -mt-2">
          <Link
            to="/passwort-vergessen"
            data-testid="login-forgot-password"
            className="font-body text-sm text-forest-700 hover:text-forest-900 hover:underline"
          >
            Passwort vergessen?
          </Link>
        </div>
      </form>

      <p className="mt-7 font-body text-sm text-[#4A524C]">
        Noch kein Konto?{" "}
        <Link
          to="/register"
          data-testid="login-go-register"
          className="text-forest-700 font-medium hover:underline"
        >
          Jetzt registrieren
        </Link>
      </p>
    </AuthShell>
  );
}

export const AuthShell = ({ children }) => (
  <div className="min-h-screen bg-bone flex flex-col">
    <header className="px-4 sm:px-8 py-6">
      <Link to="/" data-testid="auth-logo-link">
        <PlantusLogo size="md" />
      </Link>
    </header>
    <main className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-3xl border border-bone-200 p-8 sm:p-10 shadow-[0_1px_2px_rgba(20,30,20,0.04),0_10px_40px_-12px_rgba(20,30,20,0.15)]">
        {children}
      </div>
    </main>
  </div>
);

export const TextField = ({ label, testId, ...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-2">
      {label}
    </label>
    <input
      data-testid={testId}
      className="w-full font-body text-[15px] px-4 py-3 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
      {...props}
    />
  </div>
);
